"use client";
import { UserAvatar } from "@cmp-src/data-display/UserAvatar/UserAvatar";
import { HookFormTimeSelectDate } from "@cmp-src/form/TimeSelect";
import { IResource, IUserSelectable } from "@gettactic/api";
import { HookFormCheckboxInput } from "@gettactic/components/src/form/CheckboxInput";
import { HookFormToggleInput } from "@gettactic/components/src/form/ToggleInput";
import { HookFormUnifiedSelectInput } from "@gettactic/components/src/form/UnifiedSelectInput";
import { UserSelectInput } from "@gettactic/components/src/form/UserSelectInput/UserSelectInput";
import { ConfirmModal } from "@gettactic/components/src/overlay/modals/ConfirmModal";
import { useDisclosure, useStandardApiHandler } from "@gettactic/helpers";
import { cn } from "@gettactic/helpers/src/css/cn";
import { getTimeDateObjectFromString } from "@gettactic/helpers/src/date-time/getTimeDateObjectFromString";
import { useLoadingToast } from "@gettactic/helpers/src/hooks/toasts/useLoadingToast";
import {
  useAuthenticated,
  useFloors,
  useInvalidateSchedules,
  useResourceReservations,
  useUserListByIds,
  useUserListPaginated
} from "@gettactic/hooks";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ChevronDown,
  ChevronRight,
  Lock,
  MapPin,
  UserCheck,
  Users,
  X
} from "lucide-react";
import { formatInTimeZone } from "@gettactic/helpers/src/date-time/dateFnsTz";
import { getDeviceTZ } from "@gettactic/helpers/src/intl/getDeviceTZ";
import { addDays } from "date-fns";
import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { PermissionTable } from "@/features/permissions/components/PermissionsTable";
import { SharedPermissionTable } from "@/features/permissions/components/SharedPermissionsTable";
import { ToggleIsApprovablePermission } from "@/features/permissions/components/ToggleIsApprovablePermission";
import { usePermissions } from "@/features/permissions/hooks/usePermissions";
import { useResourcesPermissions } from "@/features/permissions/hooks/useResourcesPermissions";
import { AddAssignmentButton } from "@/features/reserve/components/resources/tabs/access-restrictions/AddAssignmentButton";
import { ConfirmOperationModal } from "@/features/reserve/components/resources/tabs/access-restrictions/RestrictionsTab";
import {
  ResourceSettingsActionType,
  useResourceSettingsState
} from "@/features/reserve/hooks/useResourceSettings";
import { api } from "@/lib/api/api";
import { normalizeDedicatedUsers } from "@/lib/dedicated-users";
import { useDebouncedValue } from "@/lib/hooks/useDebouncedValue";
import { areAll } from "@/lib/utils";
import {
  DEDICATE_LOOKAHEAD_DAYS,
  DedicateResourceWarning
} from "./DedicateResourceWarning";
import { DedicatedExceptionsSection } from "./DedicatedExceptionsSection";
import {
  getReservationCancelTargets,
  groupResourceReservations
} from "./groupResourceReservations";

interface AccessRestrictionsFormData {
  hasAdditionalAccess: boolean;
  startsOn: string;
  startsAt: Date;
  allUsers: boolean;
}

const STARTS_ON_ITEMS = [
  { id: "0", name: "Same Day" },
  { id: "-1", name: "1 Day Before" },
  { id: "-2", name: "2 Days Before" },
  { id: "-3", name: "3 Days Before" },
  { id: "-4", name: "4 Days Before" },
  { id: "-5", name: "5 Days Before" },
  { id: "-6", name: "6 Days Before" },
  { id: "-7", name: "1 Week Before" }
];

type AccessMode = "open-access" | "restricted-access" | "dedicated";

/** Page size for the dedicated-user picker's server-side search. */
const USER_PAGE_SIZE = 20;
const USER_SEARCH_DEBOUNCE_MS = 300;

// Lazy-loaded to break the module cycle: the embedded map pulls in the map
// view, which renders the resource card, which renders the drawer that
// hosts this assignments tab.
const MapViewEmbeddedLazy = React.lazy(() =>
  import("@/features/map/components/MapView/MapViewEmbedded").then((m) => ({
    default: m.MapViewEmbedded
  }))
);

/** 409 body of POST /resources/:id/dedicated_users when the user already
 *  has a dedicated resource — details carry that resource. */
interface DedicatedConflictBody {
  error?: {
    code?: string;
    details?: Partial<IResource> | null;
  };
}

/**
 * Management UI for a dedicated resource's user: who it belongs to, with
 * add (POST, retried with confirm_move after a conflict) and remove.
 */
const DedicatedUsersSection: React.FC<{
  resource: IResource;
  onChanged: () => void;
}> = ({ resource, onChanged }) => {
  const queryClient = useQueryClient();
  const {
    userContext: { authenticatedUser }
  } = useAuthenticated();
  const [userQuery, setUserQuery] = React.useState("");
  const [debouncedUserQuery] = useDebouncedValue(
    userQuery,
    USER_SEARCH_DEBOUNCE_MS
  );
  const userList = useUserListPaginated(debouncedUserQuery, USER_PAGE_SIZE);
  const [selectedUserId, setSelectedUserId] = React.useState("");
  const [isSaving, setIsSaving] = React.useState(false);
  const moveConfirmDisclosure = useDisclosure<{
    userId: string;
    /** Where the user is currently sitting (409 details), when known. */
    currentResource: Partial<IResource> | null;
  }>();
  const [isConflictMapOpen, setIsConflictMapOpen] = React.useState(false);
  // Overlay target for the conflict map's minimap/zoom controls, so they
  // position inside the modal's map box instead of the viewport.
  const [conflictMapControls, setConflictMapControls] =
    React.useState<HTMLDivElement | null>(null);
  const loadingToast = useLoadingToast({
    loadingMessage: "Applying changes..."
  });

  // Resolve the conflicting desk's floor/office names for the move modal.
  const conflictResource = moveConfirmDisclosure.data?.currentResource;
  const { floors: conflictFloors } = useFloors(
    conflictResource?.office_id ?? null
  );
  const conflictFloorName = conflictResource?.parent_id
    ? conflictFloors?.find((f) => f.id === conflictResource.parent_id)?.name
    : undefined;
  const conflictOfficeName = conflictResource?.office_id
    ? authenticatedUser?.offices?.byId?.[conflictResource.office_id]?.name
    : undefined;

  const dedicatedUsers = useQuery({
    queryKey: ["resources.dedicatedUsers", resource.id],
    queryFn: async () => {
      const response = await api.client.organizations.resourceDedicatedUsersGet(
        resource.id
      );
      return normalizeDedicatedUsers(response.result);
    }
  });

  // One person per desk — the section is a single slot, not a list.
  const dedicatedUser = (dedicatedUsers.data || [])[0] ?? null;

  // The users we must render are not guaranteed to be on the current search page, so fetch them by id
  const hydratedIdsKey = [
    ...new Set(
      [
        dedicatedUser?.id,
        selectedUserId,
        moveConfirmDisclosure.data?.userId
      ].filter(Boolean)
    )
  ].join(",");
  const hydratedIds = React.useMemo(
    () => (hydratedIdsKey ? hydratedIdsKey.split(",") : []),
    [hydratedIdsKey]
  );
  const hydratedUsers = useUserListByIds(hydratedIds);
  // Cache of just the pinned (dedicated/selected/conflict) users' data —
  // scoped to `hydratedIds` and pruned to it every render. This is NOT a
  // general users cache: `userList.allPages` must stay exactly the current
  // search page for the dropdown to reflect real search results, but a
  // pinned id can briefly resolve to nothing while its hydration query is
  // re-fetching (e.g. right after picking a new user), so its last-known
  // data is kept until the fresh fetch lands.
  const pinnedUsersCacheRef = React.useRef<Map<string, IUserSelectable>>(
    new Map()
  );
  const knownUsers = React.useMemo(() => {
    const cache = pinnedUsersCacheRef.current;
    const pinnedIds = new Set(hydratedIds);
    for (const id of [...cache.keys()]) {
      if (!pinnedIds.has(id)) cache.delete(id);
    }
    for (const user of (hydratedUsers.data ?? []).filter(
      (user) => user !== null
    )) {
      cache.set(user.id, user);
    }
    for (const user of userList.allPages) {
      if (pinnedIds.has(user.id)) cache.set(user.id, user);
    }

    const byId = new Map<string, IUserSelectable>();
    for (const user of cache.values()) byId.set(user.id, user);
    for (const user of userList.allPages) byId.set(user.id, user);
    return [...byId.values()];
  }, [hydratedUsers.data, userList.allPages, hydratedIds]);

  const conflictUserName = knownUsers.find(
    (u) => u.id === moveConfirmDisclosure.data?.userId
  )?.name;

  // The dedicated-users payload may carry ids only — resolve display
  // names from the users we have loaded.
  const resolveUserName = (user: { id: string; name: string }) =>
    user.name || knownUsers.find((u) => u.id === user.id)?.name || user.id;

  const refresh = async () => {
    await Promise.all([
      queryClient.invalidateQueries({
        queryKey: ["resources.dedicatedUsers", resource.id]
      }),
      queryClient.invalidateQueries({
        queryKey: ["organizations.resourcesAll"]
      })
    ]);
    onChanged();
  };

  const addUser = async (userId: string, confirmMove: boolean) => {
    setIsSaving(true);
    loadingToast.startLoading();
    const response = await api.client.organizations.resourceDedicatedUserPost(
      resource.id,
      { user_id: userId, confirm_move: confirmMove }
    );
    setIsSaving(false);

    if (response.ok) {
      loadingToast.success("Resource dedicated to the user");
      setSelectedUserId("");
      setUserQuery("");
      moveConfirmDisclosure.close();
      await refresh();
      return;
    }
    // Conflict: the user already has another dedicated resource — show
    // where they're currently sitting (409 details) and ask before moving
    // them here (retries with confirm_move: true).
    if (!confirmMove && response.status === 409) {
      loadingToast.dismiss();
      const body = response.result as DedicatedConflictBody | null;
      setIsConflictMapOpen(false);
      moveConfirmDisclosure.open({
        userId,
        currentResource: body?.error?.details ?? null
      });
      return;
    }
    loadingToast.error("Could not dedicate the resource to this user");
  };

  const removeUser = async (userId: string) => {
    setIsSaving(true);
    loadingToast.startLoading();
    const response = await api.client.organizations.resourceDedicatedUserDelete(
      resource.id,
      userId
    );
    setIsSaving(false);
    if (response.ok) {
      loadingToast.success("Dedicated user removed");
      await refresh();
    } else {
      loadingToast.error("Could not remove the dedicated user");
    }
  };

  return (
    <div className="mt-2 flex flex-col gap-2 rounded-md border border-neutral-200 bg-neutral-50 p-3">
      {dedicatedUsers.isLoading ? (
        <p className="text-neutral-400 text-xs">Loading dedicated user…</p>
      ) : dedicatedUser ? (
        <div className="flex items-center justify-between gap-2.5 rounded-md border border-neutral-200 bg-white px-2.5 py-2">
          <UserAvatar
            user={
              knownUsers.find((u) => u.id === dedicatedUser.id) ?? {
                id: dedicatedUser.id,
                name: dedicatedUser.name
              }
            }
            size="sm"
            className="flex-none"
          />
          <div className="min-w-0 flex-1">
            <p className="truncate font-medium text-neutral-800 text-sm">
              {resolveUserName(dedicatedUser)}
            </p>
            <p className="text-[11px] text-neutral-400">
              This {resource.type === "parking_space" ? "space" : "desk"}{" "}
              belongs to them — nobody else can book it.
            </p>
          </div>
          <button
            type="button"
            disabled={isSaving}
            onClick={() => removeUser(dedicatedUser.id)}
            className="flex-none rounded p-1 text-neutral-400 hover:bg-red-50 hover:text-red-600"
            title="Remove dedicated user"
          >
            <X size={13} />
          </button>
        </div>
      ) : (
        <>
          <p className="text-neutral-500 text-xs">
            Pick the person this{" "}
            {resource.type === "parking_space" ? "space" : "desk"} belongs to.
            It won't be bookable by anyone else.
          </p>
          <div className="flex items-end gap-2">
            <div className="min-w-0 flex-1">
              <UserSelectInput
                users={knownUsers}
                value={selectedUserId}
                onChangeValue={setSelectedUserId}
                placeholder="Select a user…"
                onSearchChange={setUserQuery}
                isSearching={
                  userQuery !== debouncedUserQuery ||
                  (userList.isFetching && !userList.isFetchingNextPage)
                }
                hasNextPage={userList.hasNextPage}
                fetchNextPage={userList.fetchNextPage}
                isFetchingNextPage={userList.isFetchingNextPage}
              />
            </div>
            <button
              type="button"
              disabled={!selectedUserId || isSaving}
              onClick={() => addUser(selectedUserId, false)}
              className="h-9 flex-none rounded-md bg-primary px-3 font-medium text-sm text-white hover:opacity-90 disabled:opacity-50"
            >
              Assign
            </button>
          </div>
        </>
      )}

      {dedicatedUser && (
        <DedicatedExceptionsSection
          resource={resource}
          ownerId={dedicatedUser.id}
          ownerName={resolveUserName(dedicatedUser)}
          timeZone={
            authenticatedUser?.offices?.byId?.[resource.office_id]?.time_zone ||
            getDeviceTZ() ||
            "UTC"
          }
          onChanged={onChanged}
        />
      )}

      <ConfirmModal
        isOpen={moveConfirmDisclosure.isOpen}
        onOpenChange={moveConfirmDisclosure.setOpen}
        onConfirm={() => {
          const userId = moveConfirmDisclosure.data?.userId;
          if (userId) addUser(userId, true);
        }}
        title="Move dedicated desk?"
        confirmLabel="Move here"
        message={
          <div className="flex flex-col gap-2">
            <p>
              {conflictUserName || "This user"} already has a dedicated desk
              {conflictResource ? ":" : "."}
            </p>
            {conflictResource && (
              <div className="rounded-md border border-neutral-200 bg-neutral-50 px-3 py-2">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="font-medium text-neutral-800 text-sm">
                      {conflictResource.name}
                      {conflictResource.label ? (
                        <span className="ml-2 rounded-full bg-neutral-200 px-2 py-0.5 font-normal text-[11px] text-neutral-600">
                          {conflictResource.label}
                        </span>
                      ) : null}
                    </p>
                    {(conflictFloorName || conflictOfficeName) && (
                      <p className="mt-0.5 text-neutral-500 text-xs">
                        {[conflictFloorName, conflictOfficeName]
                          .filter(Boolean)
                          .join(" · ")}
                      </p>
                    )}
                  </div>
                  {conflictResource.id &&
                    conflictResource.office_id &&
                    conflictResource.parent_id && (
                      <button
                        type="button"
                        onClick={() => setIsConflictMapOpen((v) => !v)}
                        className="inline-flex flex-none items-center gap-1 rounded-md border border-neutral-200 bg-white px-2 py-1 font-medium text-neutral-600 text-xs hover:bg-neutral-100"
                      >
                        <MapPin size={12} />
                        {isConflictMapOpen ? "Hide map" : "Show on map"}
                      </button>
                    )}
                </div>
                {isConflictMapOpen &&
                  conflictResource.id &&
                  conflictResource.office_id &&
                  conflictResource.parent_id && (
                    <div className="relative mt-2 h-[220px] overflow-hidden rounded-md border border-neutral-200 bg-white">
                      <React.Suspense
                        fallback={
                          <div className="flex h-full items-center justify-center text-neutral-400 text-xs">
                            Loading map…
                          </div>
                        }
                      >
                        <MapViewEmbeddedLazy
                          officeId={conflictResource.office_id}
                          floorId={conflictResource.parent_id}
                          focussedResourceId={conflictResource.id}
                          enableControls={false}
                          controlsPortalTarget={conflictMapControls}
                        />
                      </React.Suspense>
                      <div
                        ref={setConflictMapControls}
                        className="pointer-events-none absolute inset-0 z-10"
                      />
                    </div>
                  )}
              </div>
            )}
            <p>
              Moving them here removes that dedication. Do you want to continue?
            </p>
          </div>
        }
      />
    </div>
  );
};

/**
 * Assignments tab for the builder resource editor — same flows as the
 * legacy RestrictionsTab (access mode with confirmation, priority
 * booking options, assignment tables and modals), reorganized into the
 * panel's compact sectioned layout.
 */
export const BuilderResourceAssignments: React.FC<{
  resources: IResource[];
  mutateAll: (err: boolean) => void;
}> = ({ resources, mutateAll }) => {
  const {
    userContext: { authenticatedUser }
  } = useAuthenticated();
  const [showNonShared, setShowNonShared] = useState(false);
  const [operationModal, setOperationModal] =
    useState<ConfirmOperationModal | null>(null);
  const { resourceIds, additionalAccess, refresh } = usePermissions(
    resources.map((resource) => resource.id)
  );
  const queryClient = useQueryClient();
  const permissions = useResourcesPermissions(resources);
  const accessChangeDisclosure = useDisclosure<{ type: AccessMode }>();

  // Dedicating makes a desk unbookable, so the reservations already on it have
  // to go. Load them while the confirmation is open so the admin sees whose
  // bookings they are about to cancel — and so `handleAccessChange` has the
  // exact list to delete. Dedicating is single-resource only (the card is
  // disabled otherwise), hence resources[0].
  const isDedicateConfirmOpen =
    accessChangeDisclosure.isOpen &&
    accessChangeDisclosure.data?.type === "dedicated";
  const dedicateTarget = resources[0];
  // Reservations are office wall-clock times, so the window has to be built in
  // the resource's own office timezone. Using the browser's calendar day would,
  // near a date boundary, put today's office bookings outside the range — they
  // would never be listed, and never cancelled, on a desk about to become
  // unbookable.
  const dedicateOfficeTz =
    (dedicateTarget?.office_id
      ? authenticatedUser?.offices?.byId?.[dedicateTarget.office_id]?.time_zone
      : undefined) ||
    getDeviceTZ() ||
    "UTC";
  const dedicateRangeStart = formatInTimeZone(
    new Date(),
    dedicateOfficeTz,
    "yyyy-MM-dd"
  );
  const dedicateRangeEnd = formatInTimeZone(
    addDays(new Date(), DEDICATE_LOOKAHEAD_DAYS),
    dedicateOfficeTz,
    "yyyy-MM-dd"
  );
  const {
    data: dedicateConflicts,
    // `isFetching`, not `isLoading`: reopening the confirmation for the same
    // resource and window serves the cached list while a refetch is still in
    // flight, and `isLoading` is false there. Confirming against that stale
    // list would miss bookings made since it was fetched — on a desk that is
    // about to stop being bookable.
    isFetching: isFetchingDedicateConflicts,
    error: dedicateConflictsError
  } = useResourceReservations({
    resourceId: dedicateTarget?.id,
    start: dedicateRangeStart,
    end: dedicateRangeEnd,
    tz: dedicateOfficeTz,
    enabled: isDedicateConfirmOpen
  });

  // A recurring booking arrives as one row per occurrence — collapse each
  // series so the prompt shows its pattern once and cancels it once.
  const dedicateConflictGroups = React.useMemo(
    () => groupResourceReservations(dedicateConflicts?.schedules ?? []),
    [dedicateConflicts]
  );
  const isDedicateBlocked =
    isFetchingDedicateConflicts ||
    !!dedicateConflictsError ||
    !!dedicateConflicts?.isTruncated;
  const invalidateSchedules = useInvalidateSchedules();
  const resourceContext = useResourceSettingsState();
  const { wrapApiRequest } = useStandardApiHandler();

  const allRestricted = areAll(
    resources.map((x) => x.is_open),
    false
  );
  const allDedicated = areAll(
    resources.map((x) => !!x.is_dedicated),
    true
  );
  // is_dedicated implies is_open === false, so dedicated wins the initial
  // mode over restricted — the two are mutually exclusive in the UI.
  const derivedAccessMode: AccessMode = allDedicated
    ? "dedicated"
    : !allRestricted
      ? "open-access"
      : "restricted-access";
  const [accessMode, setAccessMode] =
    React.useState<AccessMode>(derivedAccessMode);

  // Re-sync when the resources' server flags change (refetch after edits
  // made elsewhere) — the component stays mounted, so without this the
  // highlighted card could keep showing a mode the server already left.
  const accessSignature = resources
    .map((r) => `${r.id}:${r.is_open ? 1 : 0}${r.is_dedicated ? 1 : 0}`)
    .join("|");
  // Depending on accessSignature (not the derived mode) is intentional —
  // it stands in for the resources' flag content.
  React.useEffect(() => {
    setAccessMode(derivedAccessMode);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessSignature]);

  const form = useForm<AccessRestrictionsFormData>({
    defaultValues: {
      hasAdditionalAccess: false,
      startsOn: "0",
      startsAt: new Date(),
      allUsers: false
    }
  });
  const _hasAdditionalAccess = form.watch("hasAdditionalAccess");
  const hasAdditionalAccess =
    accessMode === "restricted-access" && _hasAdditionalAccess;
  const isAllUsersEnabled = form.watch("allUsers");

  const invalidateAll = () =>
    Promise.all([
      queryClient.invalidateQueries({
        queryKey: ["organizations.resourcesAll"]
      }),
      queryClient.invalidateQueries({
        queryKey: ["organizations.resourceScheduledPermissionsAll"]
      }),
      queryClient.invalidateQueries({
        queryKey: ["organizations.getResourceAdditionalAccess"]
      }),
      queryClient.invalidateQueries({
        queryKey: ["resources.dedicatedUsers"]
      })
    ]);

  const handleAccessChange = wrapApiRequest(
    async () => {
      const mode = accessChangeDisclosure.data?.type;
      if (!mode) return;
      if (!resources.length) throw new Error("Resource not found");

      // Flip the resource state FIRST (dedicated requires is_open ===
      // false, same as restricted). If a PUT fails/throws here, nothing
      // has been deleted yet, so the server stays fully consistent —
      // still dedicated, owner intact.
      const newResources = resources.map((resource) => ({
        ...resource,
        is_open: mode === "open-access",
        is_dedicated: mode === "dedicated"
      }));
      await Promise.all(
        newResources.map((newResource) =>
          api.client.organizations.resourcePut(newResource.id, newResource)
        )
      );

      // A dedicated desk is nobody else's to book, so the reservations the
      // confirmation just listed have to go. This runs after the PUTs for the
      // same reason as the cleanup below: if it fails, the desk is dedicated
      // with stale bookings on it (visible, fixable) rather than bookings
      // deleted for a change that never landed.
      if (mode === "dedicated") {
        // One delete per entry: a recurring series is cancelled through its
        // master schedule, which removes every occurrence — including the ones
        // past the window we listed.
        // allSettled, not all: a rejected delete would otherwise abort before
        // the refresh below, while its siblings had already succeeded — the
        // map and agenda would keep showing bookings that are gone, and the
        // admin would get a generic error instead of a useful one.
        const deletions = await Promise.allSettled(
          getReservationCancelTargets(dedicateConflictGroups).map((target) =>
            api.client.schedules.delete({
              schedule: target.scheduleId,
              recurring: target.recurring,
              slug: authenticatedUser.slug ?? "",
              resourceId: target.resourceId
            })
          )
        );
        // Some deletes may have landed even if others did not, so refresh
        // before deciding what to report.
        await invalidateSchedules();
        const hasFailedDeletion = deletions.some(
          (deletion) => deletion.status === "rejected" || !deletion.value.ok
        );
        if (hasFailedDeletion) {
          await invalidateAll();
          // Returned, not thrown: this function runs through wrapApiRequest,
          // which catches a throw, logs it, and resolves to nothing — the
          // admin would see the dialog just sit there with no explanation,
          // even though the desk WAS already dedicated. ConfirmModal reads a
          // truthy return value as the error to display, so this is what
          // actually surfaces it.
          return new Error(
            "Desk dedicated, but some existing reservations could not be cancelled — please review them"
          );
        }
      }

      // Then clear the now-stale dedicated-user links. A failure here
      // leaves an orphaned link on a resource that is no longer dedicated
      // (harmless vs. "dedicated with no owner") — surface it so the
      // admin retries.
      if (mode !== "dedicated") {
        try {
          for (const resource of resources) {
            if (!resource.is_dedicated) continue;
            const current =
              await api.client.organizations.resourceDedicatedUsersGet(
                resource.id
              );
            const deletions = await Promise.all(
              normalizeDedicatedUsers(current.result).map((user) =>
                api.client.organizations.resourceDedicatedUserDelete(
                  resource.id,
                  user.id
                )
              )
            );
            if (deletions.some((deletion) => !deletion.ok)) {
              throw new Error(
                `Access updated, but the dedicated user link on ${resource.name} could not be removed — please try again`
              );
            }
          }
        } catch (error) {
          // The PUTs already landed — refresh the caches so the UI shows
          // the real server state even though the cleanup failed.
          await invalidateAll();
          throw error;
        }
      }
      newResources.forEach((newResource) => {
        resourceContext.dispatch({
          type: ResourceSettingsActionType.SetSelectedResource,
          payload: newResource
        });
      });
      // Only flip the UI mode after every API call succeeded — an early
      // flip would show the new mode while the server kept the old state.
      setAccessMode(mode);
      await invalidateAll();
      await mutateAll(true);
      accessChangeDisclosure.close();
      return;
    },
    resources.length === 1
      ? "Resource access updated"
      : "Resources access updated"
  );

  const handleUpdateOptions = form.handleSubmit(
    wrapApiRequest(async (data: AccessRestrictionsFormData) => {
      if (!resources.length) throw new Error("Resource not found");
      await Promise.all(
        resources.map((resource) =>
          api.client.organizations.setResourceAdditionalAccessOptions(
            resource.id,
            {
              enabled: data.hasAdditionalAccess,
              starts_on: Number(data.startsOn),
              starts_at: `${data.startsAt.getHours()}:${data.startsAt.getMinutes()}`,
              all_users: data.allUsers
            }
          )
        )
      );
      await invalidateAll();
    }, "Resource access updated")
  );

  // Hydrate priority-booking options from the loaded additional-access
  // config, exactly like the legacy tab.
  const sanitizedAdditionalAccess = JSON.stringify(additionalAccess);
  const sanitizedResource = JSON.stringify(resources);
  React.useEffect(() => {
    if (!additionalAccess || !additionalAccess[0].length) return;
    if (!resources[0]) return;
    if (sanitizedAdditionalAccess) {
      const nDate = getTimeDateObjectFromString(
        additionalAccess?.[0]?.[0]?.starts_at || ""
      );
      const allHasAllUsers = areAll(
        additionalAccess.map((x) => x[0]?.all_users),
        true
      );
      form.reset({
        hasAdditionalAccess: additionalAccess[0][0].enabled,
        startsOn: String(additionalAccess[0][0].starts_on || 0),
        startsAt: nDate,
        allUsers: allHasAllUsers
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sanitizedAdditionalAccess, sanitizedResource]);

  const accessCards: {
    mode: AccessMode;
    icon: React.ReactNode;
    title: string;
    description: string;
  }[] = [
    {
      mode: "open-access",
      icon: <Users size={15} />,
      title: "Open Access",
      description:
        resources.length === 1
          ? "Anybody can reserve this resource."
          : "Anybody can reserve these resources."
    },
    {
      mode: "restricted-access",
      icon: <Lock size={15} />,
      title: "Restricted Access",
      description:
        resources.length === 1
          ? "Only those assigned can reserve it."
          : "Only those assigned can reserve them."
    },
    {
      mode: "dedicated",
      icon: <UserCheck size={15} />,
      title: "Dedicated",
      description:
        resources.length === 1
          ? "Belongs to one person. Nobody can book it."
          : "Select a single resource to dedicate it."
    }
  ];

  return (
    <div className="flex h-full flex-col gap-5 overflow-y-auto pb-4">
      <div>
        <p className="mb-2 font-semibold text-[11px] text-neutral-400 uppercase tracking-wide">
          Access
        </p>
        <div className="flex flex-col gap-2">
          {accessCards.map((card) => {
            const isActive = accessMode === card.mode;
            // Dedicating is a one-resource affair — a person owns a
            // specific desk, not a batch.
            const isDisabled =
              card.mode === "dedicated" && resources.length > 1;
            return (
              <button
                key={card.mode}
                type="button"
                disabled={isDisabled}
                onClick={() =>
                  !isActive && accessChangeDisclosure.open({ type: card.mode })
                }
                className={cn(
                  "flex items-start gap-2.5 rounded-md border px-3 py-2.5 text-left transition-colors",
                  isActive
                    ? "border-primary bg-primary/5"
                    : "border-neutral-200 hover:border-neutral-300",
                  isDisabled &&
                    "cursor-not-allowed opacity-50 hover:border-neutral-200"
                )}
              >
                <span
                  className={cn(
                    "mt-0.5 flex-none",
                    isActive ? "text-primary" : "text-neutral-400"
                  )}
                >
                  {card.icon}
                </span>
                <span className="min-w-0">
                  <span
                    className={cn(
                      "block font-medium text-sm",
                      isActive ? "text-neutral-900" : "text-neutral-700"
                    )}
                  >
                    {card.title}
                  </span>
                  <span className="block text-[11px] text-neutral-400">
                    {card.description}
                  </span>
                </span>
              </button>
            );
          })}
        </div>
        {accessMode === "dedicated" && resources.length === 1 && (
          <DedicatedUsersSection
            resource={resources[0]}
            onChanged={() => mutateAll(false)}
          />
        )}
      </div>

      {/* Priority booking and assignments only make sense for bookable
          pools — a dedicated resource has exactly one owner, so both
          sections disappear entirely in dedicated mode. */}
      {accessMode !== "dedicated" && (
        <>
          <div>
            <p className="mb-2 font-semibold text-[11px] text-neutral-400 uppercase tracking-wide">
              Priority Booking
            </p>
            <HookFormToggleInput
              form={form}
              name="hasAdditionalAccess"
              label="Secondary group access"
              description="If the primary group hasn't reserved, a secondary group can gain access at a time you choose."
              onChange={() => handleUpdateOptions()}
              // Priority booking only applies to restricted pools — open
              // resources have no primary group, dedicated ones aren't
              // bookable at all.
              disabled={accessMode !== "restricted-access"}
              togglePosition="right"
            />
            {hasAdditionalAccess && (
              <div className="mt-2 flex flex-col gap-3 rounded-md border border-neutral-200 bg-neutral-50 p-3">
                <div className="flex items-start gap-3">
                  <HookFormUnifiedSelectInput
                    form={form}
                    name="startsOn"
                    label="Days"
                    items={STARTS_ON_ITEMS}
                    className="flex-1"
                    onChange={() => handleUpdateOptions()}
                  />
                  <HookFormTimeSelectDate
                    form={form}
                    name="startsAt"
                    label="Time"
                    interval={15}
                    placeholder="Select time"
                    onChange={() => handleUpdateOptions()}
                  />
                </div>
                <HookFormCheckboxInput
                  form={form}
                  name="allUsers"
                  label={
                    <>
                      Add
                      <span className="mx-1 font-bold capitalize">
                        All {authenticatedUser?.organization?.name} Users
                      </span>
                      to the secondary group
                    </>
                  }
                  onChange={() => handleUpdateOptions()}
                  checkboxPosition="left"
                />
              </div>
            )}
          </div>

          <div>
            <div className="mb-2 flex items-center justify-between">
              <p className="font-semibold text-[11px] text-neutral-400 uppercase tracking-wide">
                Assignments
              </p>
              <AddAssignmentButton
                resources={resources}
                hasAdditionalAccess={hasAdditionalAccess}
                isAllUsersEnabled={isAllUsersEnabled}
                onRefresh={() => {
                  refresh();
                  mutateAll(false);
                }}
              />
            </div>

            {permissions.shared?.length ? (
              <div className="overflow-x-auto">
                <SharedPermissionTable
                  setOperationalModal={setOperationModal}
                  data={permissions.shared}
                  inGroups={permissions.inGroups}
                  onDeleted={() => mutateAll(false)}
                />
              </div>
            ) : (
              <p className="rounded-md border border-neutral-200 border-dashed px-3 py-4 text-center text-neutral-400 text-xs">
                No assignments yet. Assign teams or users to restrict access or
                require approval.
              </p>
            )}

            {resources.length > 1 && permissions.rest.length > 0 && (
              <div className="mt-3">
                <button
                  type="button"
                  onClick={() => setShowNonShared((v) => !v)}
                  className="inline-flex items-center gap-1 font-medium text-neutral-500 text-xs hover:text-neutral-700"
                >
                  {showNonShared ? (
                    <ChevronDown size={12} />
                  ) : (
                    <ChevronRight size={12} />
                  )}
                  Non-shared assignments ({permissions.rest.length})
                </button>
                {showNonShared && (
                  <div className="mt-2 overflow-x-auto">
                    <PermissionTable
                      setOperationalModal={setOperationModal}
                      data={permissions.rest}
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        </>
      )}

      <ConfirmModal
        isOpen={accessChangeDisclosure.isOpen}
        onOpenChange={accessChangeDisclosure.setOpen}
        onConfirm={handleAccessChange}
        confirmProps={{
          disabled: isDedicateConfirmOpen && isDedicateBlocked
        }}
        confirmLabel={
          isDedicateConfirmOpen && dedicateConflictGroups.length > 0
            ? "Dedicate and cancel"
            : "Confirm"
        }
        title={
          isDedicateConfirmOpen
            ? "Make this a dedicated desk?"
            : "Change Resource's Access Type"
        }
        message={
          isDedicateConfirmOpen ? (
            <DedicateResourceWarning
              resourceName={dedicateTarget?.name || "This resource"}
              groups={dedicateConflictGroups}
              isLoading={isFetchingDedicateConflicts}
              error={dedicateConflictsError}
              isTruncated={!!dedicateConflicts?.isTruncated}
            />
          ) : (
            <>
              Are you sure you want to change the access type of the selected
              resources to{" "}
              <strong>{accessChangeDisclosure.data?.type || ""}</strong> ?
            </>
          )
        }
      />
      <ToggleIsApprovablePermission
        isOpen={
          operationModal?.operation === "is_approvable" &&
          operationModal.applyTo.length > 0
        }
        onOpenChange={(open) => {
          if (!open) {
            setOperationModal(null);
            mutateAll(false);
          }
        }}
        resourceIds={resourceIds}
        permissions={operationModal?.applyTo ?? []}
      />
    </div>
  );
};
